import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { KEY_CODES, KEY_DOWN, RESIZE, SCROLL } from '@outdoor-voices/ui/dist/config/constants';
import { ChevronDown } from '@outdoor-voices/ui';
import { memo, useCallback, useState, useRef, useEffect } from 'react';
import cc from 'classcat';
import PropTypes from 'prop-types';
import Button from '../Button';
import clickEvent from '../../utils/dom/clickEvent';
import css from './Select.module.scss';
import isVisible from '../../utils/dom/isVisible';
import Options from './Options';
import slugify from '../../utils/strings/slugify';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var Select = function Select(_ref) {
  var ariaLabel = _ref.ariaLabel,
      autoComplete = _ref.autoComplete,
      buttonProps = _ref.buttonProps,
      className = _ref.className,
      disabled = _ref.disabled,
      displayLabel = _ref.displayLabel,
      hideLabel = _ref.hideLabel,
      label = _ref.label,
      long = _ref.long,
      name = _ref.name,
      onChange = _ref.onChange,
      options = _ref.options,
      required = _ref.required,
      placeholder = _ref.placeholder,
      toggleValues = _ref.toggleValues,
      value = _ref.value;
  var buttonRef = useRef(null);
  var interval = useRef(null);
  var optionsRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      interacted = _useState4[0],
      setInteracted = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      mounted = _useState6[0],
      setMounted = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      open = _useState8[0],
      setOpen = _useState8[1];

  var _useState9 = useState({
    flip: false,
    left: 0,
    top: 0
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      position = _useState10[0],
      setPosition = _useState10[1];

  var onFocus = function onFocus() {
    setFocused(true);
  };

  var onBlur = function onBlur() {
    setFocused(false);
  };

  var getPosition = useCallback(function () {
    var current = buttonRef.current;
    var currentOptions = optionsRef.current;
    var tar = 12;

    var _current$getBoundingC = current.getBoundingClientRect(),
        buttonLeft = _current$getBoundingC.left,
        buttonTop = _current$getBoundingC.top;

    var buttonWidth = current.offsetWidth;
    var buttonHeight = current.offsetHeight;
    var optionsWidth = currentOptions.offsetWidth;
    var optionsHeight = currentOptions.offsetHeight;
    var flip = buttonTop + buttonHeight + currentOptions.offsetHeight > window.innerHeight;
    var optionsLeft = buttonLeft + buttonWidth / 2 - optionsWidth / 2;
    var optionsTop = flip ? buttonTop - optionsHeight - 12 : buttonTop + buttonHeight + 12;
    var left = Math.round(Math.max(tar, Math.min(optionsLeft, window.innerWidth - optionsWidth - tar)));
    var top = Math.max(Math.min(optionsTop, window.innerHeight - optionsHeight), 0);
    return {
      flip: flip,
      left: left,
      top: top
    };
  }, [buttonRef, optionsRef]);
  var doClose = useCallback(function () {
    var _buttonRef$current;

    setOpen(false);
    (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.querySelector('button').focus();
  }, [buttonRef]);

  var doOpen = function doOpen() {
    setInteracted(true);
    setPosition(getPosition());
    setOpen(true);
  };

  var focus = function focus() {
    var current = optionsRef.current;
    var selected = current.querySelector('[aria-selected="true"]');
    var target = selected || current.querySelector('button:not(:disabled)');
    target.focus();
  };

  var select = function select(newValue) {
    var isSame = newValue === value;
    onChange({
      target: {
        name: name,
        value: toggleValues && isSame ? false : newValue,
        label: label
      }
    });
    doClose();
  };

  var selectAutocomplete = function selectAutocomplete(newValue) {
    var option = options.find(function (option) {
      return option.value === newValue || option.label === newValue;
    });

    if (option) {
      onChange({
        target: {
          name: name,
          value: option.value,
          label: label
        }
      });
    }
  };

  var toggle = function toggle() {
    if (open) {
      doClose();
    } else {
      doOpen();
    }
  };

  var getLabel = function getLabel() {
    var _options$find;

    if (displayLabel) {
      return displayLabel;
    }

    if (value === false || value === '') {
      return placeholder;
    }

    return /*#__PURE__*/_jsx(_Fragment, {
      children: ((_options$find = options.find(function (option) {
        return option.value === value;
      })) === null || _options$find === void 0 ? void 0 : _options$find.label) || placeholder
    });
  };

  useEffect(function () {
    setMounted(true);
  }, [mounted]);
  useEffect(function () {
    var onKeyDownFocused = function onKeyDownFocused(event) {
      switch (event.key) {
        case KEY_CODES.ARROW_DOWN:
        case KEY_CODES.ARROW_UP:
          event.preventDefault();
          setPosition(getPosition());
          setOpen(true);
          break;

        default:
          break;
      }
    };

    if (focused) {
      document.addEventListener(KEY_DOWN, onKeyDownFocused, false);
    } else {
      document.removeEventListener(KEY_DOWN, onKeyDownFocused);
    }

    return function () {
      document.removeEventListener(KEY_DOWN, onKeyDownFocused);
    };
  }, [focused, getPosition]);
  useEffect(function () {
    var current = buttonRef.current;

    var focusOption = function focusOption(dir) {
      var current = optionsRef.current;
      var buttons = current.querySelectorAll('button:not(:disabled)');
      var items = Array.from(buttons);
      var index = Math.max(0, Math.min(items.indexOf(document.activeElement) + dir, items.length - 1));
      items[index].focus();
    };

    var onDraw = function onDraw() {
      if (!isVisible(current)) {
        doClose();
        return;
      }

      setPosition(getPosition());
    };

    var onKeyDown = function onKeyDown(event) {
      var ARROW_DOWN = KEY_CODES.ARROW_DOWN,
          ARROW_LEFT = KEY_CODES.ARROW_LEFT,
          ARROW_RIGHT = KEY_CODES.ARROW_RIGHT,
          ARROW_UP = KEY_CODES.ARROW_UP,
          ESC = KEY_CODES.ESC,
          TAB = KEY_CODES.TAB;

      switch (event.key) {
        case ESC:
          doClose();
          break;

        case ARROW_LEFT:
        case ARROW_UP:
          event.preventDefault();
          focusOption(-1);
          break;

        case ARROW_DOWN:
        case ARROW_RIGHT:
          event.preventDefault();
          focusOption(1);
          break;

        case TAB:
          event.preventDefault();
          break;

        default:
          break;
      }
    };

    var clearListeners = function clearListeners() {
      var _document$querySelect;

      document.removeEventListener(KEY_DOWN, onKeyDown);
      document.removeEventListener(SCROLL, onDraw);
      (_document$querySelect = document.querySelector('html')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.removeEventListener(clickEvent(), doClose);
      window.removeEventListener(RESIZE, onDraw);
    };

    if (open) {
      document.addEventListener(KEY_DOWN, onKeyDown, false);
      document.addEventListener(SCROLL, onDraw, {
        passive: true
      });
      window.addEventListener(RESIZE, onDraw, {
        passive: true
      });
      interval.current = setTimeout(function () {
        var _document$querySelect2;

        (_document$querySelect2 = document.querySelector('html')) === null || _document$querySelect2 === void 0 ? void 0 : _document$querySelect2.addEventListener(clickEvent(), doClose, false);
        focus();
      }, 150);
    } else if (interacted) {
      clearListeners();
    }

    return function () {
      clearInterval(interval.current);
      clearListeners();
    };
  }, [buttonRef, doClose, getPosition, interacted, open, optionsRef]);
  var slugName = slugify(name);
  var labelId = "select-".concat(slugName, "-label");
  var dropdownId = "select-".concat(slugName, "-dd");
  var flip = position.flip,
      left = position.left,
      top = position.top;
  return /*#__PURE__*/_jsxs("div", {
    className: cc([css.root, className, _defineProperty({}, css.open, open)]),
    children: [/*#__PURE__*/_jsx("button", {
      "aria-label": ariaLabel || label,
      className: cc([css.label, {
        'ada-hidden': hideLabel
      }]),
      id: labelId,
      onClick: doOpen,
      tabIndex: hideLabel ? -1 : 0,
      type: "button",
      children: label || ariaLabel
    }), required && /*#__PURE__*/_jsx("input", {
      "aria-hidden": "true",
      "aria-labelledby": labelId,
      autoComplete: autoComplete,
      className: css.autoComplete,
      "data-testid": "select-testid",
      onChange: function onChange(event) {
        return selectAutocomplete(event.target.value);
      },
      required: required,
      tabIndex: -1,
      type: "text",
      value: value ? String(value) : ''
    }), /*#__PURE__*/_jsx("div", {
      className: css.button,
      ref: buttonRef,
      children: /*#__PURE__*/_jsx(Button, _objectSpread(_objectSpread({}, buttonProps), {}, {
        "aria-expanded": open,
        "aria-labelledby": labelId,
        "aria-haspopup": "listbox",
        "aria-controls": dropdownId,
        disabled: disabled,
        iconRight: /*#__PURE__*/_jsx(ChevronDown, {}),
        name: name,
        onBlur: onBlur,
        onClick: toggle,
        onFocus: onFocus,
        role: "combobox",
        title: ariaLabel || label,
        children: getLabel()
      }))
    }), mounted && /*#__PURE__*/_jsx(Options, {
      flip: flip,
      left: left,
      long: long,
      id: dropdownId,
      onChange: select,
      open: open,
      options: options,
      ref: optionsRef,
      top: top,
      value: value
    })]
  });
};

Select.defaultProps = {
  ariaLabel: false,
  autoComplete: '',
  buttonProps: {
    gray: true
  },
  className: '',
  disabled: false,
  displayLabel: false,
  hideLabel: true,
  long: false,
  placeholder: 'Select',
  required: false,
  toggleValues: false,
  value: false
};
Select.propTypes = {
  ariaLabel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  autoComplete: PropTypes.string,
  buttonProps: PropTypes.shape(),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  displayLabel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  hideLabel: PropTypes.bool,
  label: PropTypes.string.isRequired,
  long: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.element]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  })).isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  toggleValues: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool, PropTypes.number, PropTypes.string])
};
export default /*#__PURE__*/memo(Select);